<template>
  <Wrapper pageIcon="server-management">
    <template slot="aboveContent">
      <Alert class="error">
        <p>
          You are responsible for your game server account. <br />
          These are also referred to as 'tokens'. <br />
          Banned game server accounts/tokens may be restricted from<br />
          playing games that are associated with this account/token.
        </p>
        <p>
          Please do not provide game server login tokens to a third party.<br />
          If you have done so in the past, we strongly advise you to delete the
          token by resetting your password and delete your tokens.<br />
          This is to prevent misuse by those who may have had unauthorized
          access to your account.
        </p>
      </Alert>
      <Divider size="2em" />
    </template>
    <h3 class="title">
      Here you can manage dedicated game server tokens.
    </h3>
    <form name="create-token" v-on:submit.prevent="createToken">
      <grid horizontal="middle">
        <grid-item grow="1">
          <FieldWrapper v-if="appIds">
            <select
              name="appId"
              v-model="form.appId"
              v-validate="validatorDefinitions.appId"
              data-vv-scope="bgslt"
            >
              <option value="">Select Game</option>
              <option v-for="appId in appIds" :value="appId.id">{{
                appId.name
              }}</option>
            </select>
            <Icon slot="icon" class="field-icon" type="down" />
            <div slot="errors" v-if="errors.has('appId', 'bgslt')">
              {{ errors.first("appId", "bgslt") }}
            </div>
          </FieldWrapper>
        </grid-item>
        <grid-item grow="2">
          <FieldWrapper>
            <input
              v-validate="validatorDefinitions.bgsltDescription"
              name="description"
              v-model="form.description"
              maxlength="25"
              placeholder="Description eg. Server 1"
              data-vv-scope="bgslt"
            />
            <div slot="errors" v-if="errors.has('description', 'bgslt')">
              {{ errors.first("description", "bgslt") }}
            </div>
          </FieldWrapper>
        </grid-item>
        <grid-item>
          <DecoratedButton type="submit" :loading="creating">
            CREATE TOKEN
          </DecoratedButton>
        </grid-item>
      </grid>
    </form>
    <template slot="belowContent">
      <Divider size="2em" />
      <SectionWrapper class="whiteframe shadowframe">
        <h3 v-if="(!tokens || tokens.lenght === 0) && !loading">
          You don't have any tokens at the moment
        </h3>
        <template v-else>
          <table class="table">
            <thead>
              <tr>
                <th>
                  GAME
                </th>
                <th>
                  Token
                </th>
                <th>
                  Activated
                </th>
                <th>
                  Last Used
                </th>
                <th>
                  Description
                </th>
                <th>
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="token in tokens">
                <td>
                  {{ appIds[token.app_id].name }}
                </td>
                <td>
                  <pre class="code">{{ token.code }}</pre>
                </td>
                <td>
                  <Icon
                    :type="token.active ? 'check' : 'x'"
                    :color="token.active ? 'green' : 'red'"
                  />
                </td>
                <td>
                  <time
                    :datetime="token.last_logged_at"
                    :title="token.last_logged_at"
                    >{{ formatDate(token.last_logged_at) }}</time
                  >
                </td>
                <td>
                  <div :title="token.description" class="truncate">
                    {{ token.description }}
                  </div>
                </td>
                <td>
                  <DecoratedButton
                    type="button"
                    class="short"
                    @click="regenerateToken(token)"
                    v-tooltip="'Regenerate Token'"
                  >
                    <Icon type="refresh" />
                  </DecoratedButton>
                  <DecoratedButton
                    type="button"
                    class="short error"
                    @click="deleteToken(token)"
                    v-tooltip="'Delete Token'"
                  >
                    <Icon type="trash" />
                  </DecoratedButton>
                </td>
              </tr>
            </tbody>
          </table>
        </template>
      </SectionWrapper>
    </template>
  </Wrapper>
</template>

<script>
import { mapGetters } from "vuex";

import Loader from "vue-spinner/src/BounceLoader";

import Wrapper from "@/components/partials/profile/Wrapper";

import moment from "moment";

export default {
  name: "server-management",
  components: { Wrapper, Loader },
  data: function() {
    return {
      appIds: {
        STOREYLANDS001: {
          name: "Ylands",
          id: "STOREYLANDS001"
        }
      },
      form: {
        appId: "",
        description: ""
      },
      loading: false,
      creating: false,
      tokens: null
    };
  },
  computed: {
    ...mapGetters("user", {
      user: "getUser"
    })
  },
  methods: {
    resetForm() {
      Object.keys(this.fields.$bgslt).some(
        key => (this.fields.$bgslt[key].dirty = false)
      );
      this.form.appId = "";
      this.form.description = "";
      setTimeout(() => {
        this.$validator.resume();
      }, 400);
    },
    getTokens() {
      this.loading = true;
      this.services.BGSLTResource.get()
        .then(response => {
          this.tokens = response.body;
          this.loading = false;
        })
        .catch(err => {
          this.loading = false;
        });
    },
    createToken() {
      this.$validator.validateAll("bgslt").then(valid => {
        if (valid) {
          this.$validator.pause();
          this.creating = true;
          this.services.BGSLTResource.save({
            appId: this.form.appId,
            description: this.form.description
          })
            .then(response => {
              this.creating = false;
              this.getTokens();
            })
            .catch(err => {
              this.creating = false;
              this.getTokens();
            });
          this.resetForm();
        }
      });
    },
    updateToken(token) {
      if (confirm("Are you sure you want to regenerate this token?") == true) {
        this.services.BGSLTResource.update(
          {
            id: token.code
          },
          {
            description: token.description
          }
        ).then(() => {
          this.getTokens();
        });
      }
    },
    regenerateToken(token) {
      if (confirm("Are you sure you want to regenerate this token?") == true) {
        this.services.BGSLTResource.regenerate(
          {
            id: token.code
          },
          {}
        ).then(() => {
          this.getTokens();
        });
      }
    },
    deleteToken(token) {
      if (confirm("Are you sure you want to delete this token?") == true) {
        this.services.BGSLTResource.delete(
          {
            id: token.code
          },
          {}
        ).then(() => {
          this.getTokens();
        });
      }
    },
    formatDate(date) {
      if (!date) {
        return "Never";
      }

      return moment(date).fromNow();
    }
  },
  created: function() {
    this.getTokens();
  }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
.wrapper {
  min-width: 400px;
}

.title {
  margin-top: 0;
}

.code {
  margin: 0;
  padding: 0;
  font-weight: bold;
}

.truncate {
  max-width: 100px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.table {
  width: 100%;

  thead {
    th {
      padding: 1em;
      border-bottom: 2px solid #323232;
    }
  }

  tbody {
    tr {
      td {
        vertical-align: middle;
        padding: 1em 0.5em;
        border-bottom: 1px solid #f5f5f5;
      }

      &:last-child {
        td {
          border: 0;
        }
      }
    }
  }
}
</style>
